import { useService } from '@toeverything/infra';
import { useEffect, useState } from 'react';

import type { Citation, CitationDocRef } from '../orchestration/types';
import { WorkspaceService } from '../workspace';
import { WorkspaceCitationStore } from './workspace-citation-store';

/**
 * Read-side hook for the workspace citation store. Consumers:
 *   - Citations menu page (list/filter/export)
 *   - Manuscript bibliography block
 *   - Branch-create modal (seed citations into the fork)
 *
 * Phase 7: also triggers `hydrate(workspaceId)` once per workspace
 * so a hard refresh restores citations from Postgres.
 */
export function useWorkspaceCitations(): {
  citations: Citation[];
  loading: boolean;
  getById: (id: string) => Citation | undefined;
  getReferences: (citationId: string) => Promise<CitationDocRef[]>;
} {
  const store = useService(WorkspaceCitationStore);
  const workspaceService = useService(WorkspaceService);
  const [citations, setCitations] = useState<Citation[]>(store.citations);
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    const sub = store.citations$.subscribe(setCitations);
    return () => sub.unsubscribe();
  }, [store]);

  useEffect(() => {
    const sub = store.loading$.subscribe(setLoading);
    return () => sub.unsubscribe();
  }, [store]);

  useEffect(() => {
    const workspaceId = workspaceService.workspace?.id;
    if (!workspaceId) return;
    void store.hydrate(workspaceId);
  }, [store, workspaceService]);

  return {
    citations,
    loading,
    getById: id => store.getById(id),
    getReferences: id =>
      store.getReferences(workspaceService.workspace.id, id),
  };
}
